/**
 * Pomocniki kontroli wersji — WYLACZNIE panel administratora.
 *
 * ZRODLA DANYCH (wszystkie pod @require_admin — pracownik ich nie zobaczy):
 *   GET /api/wdrozenia/aktualna  -> { wersja, commit, commit_data, srodowisko,
 *                                     ostatnie_wdrozenie: <wpis>|null }
 *   GET /api/wdrozenia?limit=&srodowisko=  -> { items: [<wpis>], count }
 *
 * Ksztalt <wpis> (backend/api/wdrozenia.py, _wpis_na_slownik):
 *   { id, wersja, commit, commit_krotki, poprzedni_commit, srodowisko,
 *     tytul, opis, wdrozyl, automatyczny, wdrozono_at }
 *
 * Zrodlo zapasowe numeru wersji:
 *   GET /api/info (@require_auth) -> { name, version, endpoints }
 * Uzywane tylko wtedy, gdy /wdrozenia/aktualna nie odpowie (np. zanim na
 * serwerze wykona sie migracja 037).
 *
 * NIE korzystamy z /api/health — ten endpoint jest publiczny (bez auth), a
 * wlasciciel chce, zeby informacje o wersji zostaly w panelu administratora.
 *
 * Odpowiedzi backendu maja koperte { success, data, message } (utils/database.py).
 * axios NIE ma pola res.ok — sprawdzamy res.data.success, nie res.ok.
 */

// CICHE ZADANIA (konwencja z services/komunikatorService.js).
// Bez tego interceptor w services/api.js PONAWIA kazdy blad 5xx dwa razy
// (1500 ms + 3000 ms) i dorzuca czerwony toast 'Blad serwera'. Tutaj to szkodzi:
// brak tabeli 'wdrozenia' (przed migracja 037) albo chwilowa awaria bazy dawaly
// adminowi kilka sekund martwego czasu i kilka dymkow naraz, mimo ze komponent
// ma wlasny, lagodny komunikat o bledzie.
const CICHY = { silent: true, noRetry: true };

// Nazwy srodowisk zgodne z backend/utils/wersja.py: test / produkcja / lokalne
export const SRODOWISKA = {
  produkcja: {
    etykieta: 'PRODUKCJA',
    tlo: 'var(--danger)',
    tekst: '#fff',
    opis: 'Serwer produkcyjny — pracuje na nim cała firma'
  },
  test: {
    etykieta: 'TEST',
    tlo: 'var(--warning)',
    tekst: '#212529',
    opis: 'Serwer testowy — zmiany nie dotykają danych produkcyjnych'
  },
  lokalne: {
    etykieta: 'LOKALNE',
    tlo: 'var(--text-muted)',
    tekst: '#fff',
    opis: 'Uruchomienie lokalne, poza serwerem'
  },
  nieznane: {
    etykieta: 'NIEZNANE',
    tlo: 'var(--text-muted)',
    tekst: '#fff',
    opis: 'Nie udało się ustalić środowiska'
  }
};

/** Sprowadza rozne zapisy srodowiska do klucza z SRODOWISKA. */
export const normalizujSrodowisko = (wartosc) => {
  if (!wartosc || typeof wartosc !== 'string') return 'nieznane';
  const w = wartosc.trim().toLowerCase();
  if (['produkcja', 'produkcyjne', 'prod', 'production'].includes(w)) return 'produkcja';
  if (['test', 'testowe', 'testing', 'staging'].includes(w)) return 'test';
  if (['lokalne', 'lokalnie', 'local', 'development', 'dev'].includes(w)) return 'lokalne';
  return 'nieznane';
};

/** Opis srodowiska (etykieta + kolory) gotowy do wyswietlenia. */
export const opisSrodowiska = (wartosc) => SRODOWISKA[normalizujSrodowisko(wartosc)];

/** Skrocony skrot commita — adminowi wystarczy 8 znakow. */
export const skrocCommit = (commit, dlugosc = 8) => {
  if (!commit || typeof commit !== 'string') return null;
  const oczyszczony = commit.trim();
  return oczyszczony ? oczyszczony.slice(0, dlugosc) : null;
};

/** Data w formacie polskim; puste/bledne wejscie daje '—'. */
export const formatujDate = (wartosc, zGodzina = true) => {
  if (!wartosc) return '—';
  const data = new Date(wartosc);
  if (Number.isNaN(data.getTime())) return '—';
  const opcje = { day: '2-digit', month: '2-digit', year: 'numeric' };
  if (zGodzina) {
    opcje.hour = '2-digit';
    opcje.minute = '2-digit';
  }
  return data.toLocaleDateString('pl-PL', opcje);
};

/** Wiersz historii wdrozen -> jeden staly ksztalt dla widoku. */
export const normalizujWdrozenie = (wiersz) => {
  if (!wiersz || typeof wiersz !== 'object') return null;

  const commit = wiersz.commit || wiersz.commit_hash || null;

  return {
    id: wiersz.id != null ? wiersz.id : commit,
    wersja: wiersz.wersja || null,
    commit,
    commitKrotki: wiersz.commit_krotki || skrocCommit(commit),
    poprzedniCommit: wiersz.poprzedni_commit || null,
    srodowisko: normalizujSrodowisko(wiersz.srodowisko),
    tytul: wiersz.tytul || '(bez tytułu)',
    opis: typeof wiersz.opis === 'string' ? wiersz.opis : '',
    wdrozyl: wiersz.wdrozyl || null,
    // automatyczny = wpis zrobil deploy.sh; false = dopisany ręcznie
    automatyczny: wiersz.automatyczny !== false,
    data: wiersz.wdrozono_at || null
  };
};

/**
 * Aktualna wersja tej instancji + ostatnie zapisane wdrozenie.
 * Nigdy nie rzuca — przy bledzie zwraca ksztalt z pustymi polami i 'blad'.
 *
 * @param {object} api - instancja axios (services/api)
 */
export const pobierzAktualnaWersje = async (api) => {
  const pusty = {
    wersja: null,
    commit: null,
    commitKrotki: null,
    commitData: null,
    srodowisko: 'nieznane',
    ostatnieWdrozenie: null,
    blad: null
  };

  try {
    const odp = await api.get('/wdrozenia/aktualna', CICHY);
    // axios: sprawdzamy koperte odpowiedzi, NIE odp.ok (takiego pola nie ma)
    if (odp && odp.data && odp.data.success && odp.data.data) {
      const d = odp.data.data;
      return {
        wersja: d.wersja || null,
        commit: d.commit || null,
        commitKrotki: skrocCommit(d.commit),
        commitData: d.commit_data || null,
        srodowisko: normalizujSrodowisko(d.srodowisko),
        ostatnieWdrozenie: normalizujWdrozenie(d.ostatnie_wdrozenie),
        blad: null
      };
    }
  } catch (blad) {
    console.error('Blad pobierania /wdrozenia/aktualna:', blad);
  }

  // Zapas: sam numer wersji z /info (dziala nawet bez tabeli 'wdrozenia')
  try {
    const odp = await api.get('/info', CICHY);
    if (odp && odp.data && odp.data.version) {
      return { ...pusty, wersja: odp.data.version };
    }
  } catch (blad) {
    console.error('Blad pobierania /info:', blad);
  }

  return { ...pusty, blad: 'Nie udało się pobrać informacji o wersji' };
};

/**
 * Historia wdrozen (lista). Nigdy nie rzuca — przy bledzie zwraca pusta liste.
 *
 * @param {object} api - instancja axios (services/api)
 * @param {object} opcje - { limit, srodowisko } ; srodowisko: 'test' | 'produkcja'
 */
export const pobierzHistorieWdrozen = async (api, opcje = {}) => {
  const limit = opcje.limit || 50;
  const parametry = [`limit=${encodeURIComponent(limit)}`];
  // backend przyjmuje tylko 'test' i 'produkcja'; inne wartosci daja 400
  if (opcje.srodowisko === 'test' || opcje.srodowisko === 'produkcja') {
    parametry.push(`srodowisko=${encodeURIComponent(opcje.srodowisko)}`);
  }

  try {
    const odp = await api.get(`/wdrozenia?${parametry.join('&')}`, CICHY);
    if (odp && odp.data && odp.data.success && odp.data.data) {
      const pozycje = Array.isArray(odp.data.data.items) ? odp.data.data.items : [];
      return { pozycje: pozycje.map(normalizujWdrozenie).filter(Boolean), blad: null };
    }
    return { pozycje: [], blad: 'Nie udało się pobrać historii wdrożeń' };
  } catch (blad) {
    console.error('Blad pobierania /wdrozenia:', blad);
    // Brak tabeli (przed migracją 037) nie może wywalić zakładki
    return { pozycje: [], blad: 'Nie udało się pobrać historii wdrożeń' };
  }
};
